"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AutomationOutputFieldMissingError = exports.AUTOMATION_TARGET_CONTEXT_FIELD = void 0;
exports.readAutomationOutputField = readAutomationOutputField;
exports.automationTargetAlias = automationTargetAlias;
exports.withAutomationTargetContext = withAutomationTargetContext;
exports.automationFanoutData = automationFanoutData;
exports.automationCellValue = automationCellValue;
exports.mapAutomationTargetValues = mapAutomationTargetValues;
exports.automationMatchKeyValues = automationMatchKeyValues;
/**
 * How the answer of an automation's workflow becomes the cells of the rows
 * it writes: which field of the answer each column reads, the rows written to
 * earlier tables (`__targets`), one row per item of a list, and the values a
 * row is matched by. Pure rules: the platform's OutputWriter and the local
 * run of `sl-streamkit` write with them, each into its own tables.
 */
const index_1 = require("./index");
/** The field of the answer that holds the rows written to earlier tables. */
exports.AUTOMATION_TARGET_CONTEXT_FIELD = '__targets';
/** A mapped field the answer does not hold. */
class AutomationOutputFieldMissingError extends Error {
    constructor(sourceField, targetColumnId, targetColumnName, availableFields) {
        super(`Output mapping source field "${sourceField}" was not produced for target column "${targetColumnName ?? targetColumnId}". Available fields: ${availableFields.join(', ') || 'none'}`);
        this.sourceField = sourceField;
        this.targetColumnId = targetColumnId;
        this.targetColumnName = targetColumnName;
        this.availableFields = availableFields;
        this.name = 'AutomationOutputFieldMissingError';
    }
}
exports.AutomationOutputFieldMissingError = AutomationOutputFieldMissingError;
/** The value at `path` (`a.b`, `a[0].b`) of the answer, or undefined. */
function readAutomationOutputField(data, path) {
    if (!path || !data)
        return undefined;
    let current = data;
    for (const part of path.replace(/\[(\d+)\]/g, '.$1').split('.')) {
        if (current == null || typeof current !== 'object')
            return undefined;
        current = current[part];
    }
    return current;
}
/** The alias later tables read an output table's rows by. */
function automationTargetAlias(target) {
    return target.targetAlias?.trim() || target.tableId;
}
/** The answer as a table reads it: with the rows written to earlier tables. */
function withAutomationTargetContext(data, targets) {
    return { ...data, [exports.AUTOMATION_TARGET_CONTEXT_FIELD]: targets };
}
/** The answer one item of a list writes its row from. */
function automationFanoutData(parent, item, index) {
    const itemObject = item && typeof item === 'object' && !Array.isArray(item)
        ? item
        : {};
    return {
        ...parent,
        item,
        value: item,
        __item: item,
        __parent: parent,
        __index: index,
        ...itemObject,
    };
}
/** A file or list of files of the answer as an attachment cell; any other value as it is. */
function automationCellValue(value) {
    const file = (ref) => ({
        blobId: ref.blobId,
        name: ref.filename,
        mime: ref.mimeType,
        size: ref.size,
    });
    if ((0, index_1.isBlobReference)(value))
        return { _type: 'attachment', files: [file(value)] };
    if (Array.isArray(value) && value.length > 0 && value.every(index_1.isBlobReference))
        return { _type: 'attachment', files: value.map(file) };
    return value;
}
/**
 * The cells an answer gives a table, by column, before cell conversion, and
 * the relation mappings whose row the writer looks up with `lookups[].value`.
 * Without mappings the fields of the answer are the columns; a table that
 * matches its row without mappings writes only its keys (`automationMatchKeyValues`).
 * Throws `AutomationOutputFieldMissingError` for a mapped field the answer lacks.
 */
function mapAutomationTargetValues(target, data) {
    if (!target.fieldMappings?.length) {
        if (target.matchConfigId)
            return { values: {}, lookups: [] };
        const values = { ...data };
        delete values[exports.AUTOMATION_TARGET_CONTEXT_FIELD];
        return { values, lookups: [] };
    }
    const values = {};
    const lookups = [];
    for (const mapping of target.fieldMappings) {
        if (mapping.fixedValue) {
            values[mapping.targetColumnId] = mapping.fixedValue;
            continue;
        }
        const value = readAutomationOutputField(data, mapping.sourceField);
        if (value === undefined && mapping.sourceField)
            throw new AutomationOutputFieldMissingError(mapping.sourceField, mapping.targetColumnId, mapping.targetColumnName ?? null, Object.keys(data).slice(0, 10));
        if (mapping.matchConfigId)
            lookups.push({ mapping, value });
        else
            values[mapping.targetColumnId] = value;
    }
    return { values, lookups };
}
/**
 * What the match keys look a row up by, as cells of a row created because
 * nothing matched; keys without a value are left out.
 */
function automationMatchKeyValues(keys, data) {
    const values = {};
    for (const key of keys) {
        const value = readAutomationOutputField(data, key.sourceField);
        if (value !== undefined && value !== null)
            values[key.columnId] = value;
    }
    return values;
}
